import { useState, useEffect, useCallback, useMemo } from 'react';

export interface SearchResult {
  id: string;
  type: 'card' | 'deck' | 'realm' | 'media' | 'tabletop' | 'rule' | 'page' | 'setting' | 'player';
  title: string;
  subtitle?: string;
  content?: string;
  imageUrl?: string;
  url: string;
  metadata?: Record<string, any>;
  score: number;
  realm?: string;
  tags: string[];
}

export interface SearchFilters {
  types?: string[];
  realm?: string;
  rarity?: string;
  cost?: number;
  [key: string]: any;
}

export interface SearchOptions {
  debounceMs?: number;
  limit?: number;
  minQueryLength?: number;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  types: string[];
  filters: SearchFilters;
  pagination: {
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

const DEFAULT_OPTIONS: SearchOptions = {
  debounceMs: 300,
  limit: 50,
  minQueryLength: 2
};

/**
 * Comprehensive search hook that searches across all content types
 * Replaces the slow useCards hook with server-side search
 */
export const useComprehensiveSearch = (
  initialQuery: string = '',
  initialFilters: SearchFilters = {},
  options: SearchOptions = {}
) => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Debounced search function
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, config.debounceMs);

    return () => clearTimeout(timer);
  }, [query, config.debounceMs]);

  // Build search URL with parameters
  const buildSearchUrl = useCallback((searchQuery: string, searchFilters: SearchFilters, searchOffset: number = 0) => {
    const params = new URLSearchParams();
    
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    
    if (searchFilters.types && searchFilters.types.length > 0) {
      params.append('types', searchFilters.types.join(','));
    } else {
      params.append('types', 'all');
    }
    
    if (searchFilters.realm) {
      params.append('realm', searchFilters.realm);
    }
    
    params.append('limit', config.limit!.toString());
    params.append('offset', searchOffset.toString());
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}/api/search/comprehensive?${params.toString()}`;
  }, []); // Remove config.limit dependency to prevent recreation

  // Execute search
  const executeSearch = useCallback(async (searchQuery: string, searchFilters: SearchFilters, searchOffset: number = 0, append: boolean = false) => {
    // Skip search if query is too short
    if (searchQuery && searchQuery.length < config.minQueryLength!) {
      setResults([]);
      setTotal(0);
      setHasMore(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = buildSearchUrl(searchQuery, searchFilters, searchOffset);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }
      
      const data: SearchResponse = await response.json();
      
      if (append) {
        setResults(prev => [...prev, ...data.results]);
      } else {
        setResults(data.results);
        setOffset(searchOffset);
      }
      
      setTotal(data.total);
      setHasMore(data.pagination.hasMore);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      if (!append) {
        setResults([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  }, []); // Remove all dependencies to prevent recreation

  // Main search effect
  useEffect(() => {
    executeSearch(debouncedQuery, filters, 0, false);
  }, [debouncedQuery, filters]); // Remove executeSearch from dependencies

  // Load more results
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const newOffset = offset + config.limit!;
      executeSearch(debouncedQuery, filters, newOffset, true);
    }
  }, [loading, hasMore, offset, config.limit, debouncedQuery, filters]); // Remove executeSearch from dependencies

  // Update query
  const updateQuery = useCallback((newQuery: string) => {
    setQuery(newQuery);
    setOffset(0);
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters: SearchFilters) => {
    setFilters(newFilters);
    setOffset(0);
  }, []);

  // Clear search
  const clear = useCallback(() => {
    setQuery('');
    setFilters({});
    setResults([]);
    setTotal(0);
    setHasMore(false);
    setOffset(0);
    setError(null);
  }, []);

  // Memoized results grouped by type
  const resultsByType = useMemo(() => {
    const grouped: Record<string, SearchResult[]> = {};
    
    results.forEach(result => {
      if (!grouped[result.type]) {
        grouped[result.type] = [];
      }
      grouped[result.type].push(result);
    });
    
    return grouped;
  }, [results]);

  // Search suggestions based on popular searches and content
  const getSuggestions = useCallback(async (partialQuery: string): Promise<string[]> => {
    if (!partialQuery || partialQuery.length < 2) {
      return [];
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseUrl}/api/search/suggestions?query=${encodeURIComponent(partialQuery)}`);
      if (response.ok) {
        const data = await response.json();
        return data.suggestions || [];
      }
    } catch (err) {
      console.warn('Failed to fetch suggestions:', err);
    }
    
    return [];
  }, []);

  return {
    // State
    query,
    filters,
    results,
    resultsByType,
    loading,
    error,
    total,
    hasMore,
    offset,
    
    // Actions
    updateQuery,
    updateFilters,
    loadMore,
    clear,
    getSuggestions,
    
    // Utilities
    isSearchActive: query.length >= config.minQueryLength!,
    isEmpty: results.length === 0 && !loading,
    hasResults: results.length > 0
  };
};

/**
 * Simplified hook for basic card search (backward compatibility)
 */
export const useCardSearch = (query: string = '', filters: SearchFilters = {}) => {
  const cardFilters = { ...filters, types: ['card'] };
  const search = useComprehensiveSearch(query, cardFilters, { limit: 100 });
  
  return {
    cards: search.results,
    loading: search.loading,
    error: search.error,
    total: search.total,
    updateQuery: search.updateQuery,
    updateFilters: (newFilters: SearchFilters) => search.updateFilters({ ...newFilters, types: ['card'] }),
    loadMore: search.loadMore,
    hasMore: search.hasMore
  };
};

/**
 * Hook for deck search
 */
export const useDeckSearch = (query: string = '', filters: SearchFilters = {}) => {
  const deckFilters = { ...filters, types: ['deck'] };
  const search = useComprehensiveSearch(query, deckFilters, { limit: 50 });
  
  return {
    decks: search.results,
    loading: search.loading,
    error: search.error,
    total: search.total,
    updateQuery: search.updateQuery,
    updateFilters: (newFilters: SearchFilters) => search.updateFilters({ ...newFilters, types: ['deck'] }),
    loadMore: search.loadMore,
    hasMore: search.hasMore
  };
};

export default useComprehensiveSearch;